// React component for the schedule information of a stop

import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import StringUtils from '../../../../utils/stringUtils';
import DateUtils from '../../../../utils/dateUtils';
import useAuth from '../../../../providers/Auth/Auth.provider';
import { STOP_TYPE } from '../../../../redux/api/load/load.model';
import CheckedDateTime from '../../../TwStop/components/CheckedDateTime';
import { USER_ROLE } from '../../../../providers/User/User.model';
import {
  openEditDateTime,
  openEditLoad,
  openEditTrip,
  setAnchorId
} from '../../../../redux/features/navigation/navigationSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  EditStopDateTimeConfig,
  setEditDateTimeConfig
} from '../../../../redux/features/publicLoad/publicLoadSlice';
import { StopStatus, TripStop } from '../../../../redux/api/trips/trips.model';
import { updateTripId } from '../../../../redux/features/trip/tripSlice';

export interface ScheduleInfoProps {
  /**
   * Previous stop
   */
  prevStop?: TripStop;
  /**
   * Stop
   * */
  stop?: TripStop;
  /**
   * Next stop
   */
  nextStop?: TripStop;
  isCustomerLink?: boolean;
  showInAndOutTimes?: boolean;
}

const getStopLabelType = (stopType: STOP_TYPE): string => {
  switch (stopType) {
    case STOP_TYPE.PICKUP:
      return 'Pick-up # ';
    case STOP_TYPE.DROP_OFF:
      return 'Delivery # ';
    case STOP_TYPE.TRANSFER:
      return 'Transfer # ';
    default:
      return '';
  }
};

const ScheduleInfo: React.FC<ScheduleInfoProps> = ({
  prevStop,
  stop,
  nextStop,
  isCustomerLink,
  showInAndOutTimes
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();

  const isEnabledShowCheckInOutTimes = getTenantSetting('customer-display-in-out-times');
  const enableLtl = userData?.tenant?.ltl_enabled || false;

  const publicLoadId = useAppSelector((state) => state.publicLoad.loadId);

  const use24HourClock = getTenantSetting('use-24-hour-clock');
  const isAppointmentWindowEnabled = getTenantSetting('enable-appointment-window');
  // Note: customer link and customer contact are not the same
  const isCustomerContact = userData?.role === USER_ROLE.CUSTOMER_CONTACT;

  const isAdminOwnerDispatcher =
    userData?.role === USER_ROLE.ADMIN ||
    userData?.role === USER_ROLE.OWNER ||
    userData?.role === USER_ROLE.DISPATCHER;

  const handleEditDateTimeClick = (
    stop: TripStop,
    type: 'in' | 'out',
    otherStops?: { prevStop?: TripStop; nextStop?: TripStop }
  ): void => {
    const { prevStop, nextStop } = otherStops || {};
    const editDateTimeConfig: EditStopDateTimeConfig = {
      title: `Edit actual ${type} time`,
      dateInputLabel: `Actual ${type} date`,
      dateTime: new Date((type === 'in' ? stop.in_time : stop.out_time) as string),
      type,
      load_stop_id: stop.id!,
      segment_id: stop.trip_id!,
      stop_updated_at: stop.updated_at
    };

    // Save current stop OUT time if selected time to edit is IN
    if (type === 'in' && stop.out_time) {
      editDateTimeConfig.nextDateTime = new Date(stop.out_time as string);
    }

    // Save current stop IN time if selected time to edit is OUT
    if (type === 'out' && stop.in_time) {
      editDateTimeConfig.prevDateTime = new Date(stop.in_time as string);
    }

    // Save previous stop OUT time if selected stop time to edit is IN
    if (prevStop && type === 'in') {
      editDateTimeConfig.prevStopOutTime = new Date(prevStop.out_time as string);
    }

    // Save next stop IN time if selected stop time to edit is OUT
    if (nextStop && type === 'out') {
      editDateTimeConfig.nextStopInTime = nextStop.in_time
        ? new Date(nextStop.in_time as string)
        : undefined;
    }

    dispatch(setEditDateTimeConfig(editDateTimeConfig));

    if (isAdminOwnerDispatcher && !publicLoadId) {
      const anchorId = `${type === 'in' ? 'stop-check-in-time-' : 'stop-check-out-time-'}${
        stop.trip_id
      }-${stop.id}`;
      dispatch(setAnchorId(anchorId));

      if (enableLtl) {
        dispatch(updateTripId(stop.trip_id || undefined));
        dispatch(openEditTrip(true));
      } else {
        dispatch(openEditLoad());
      }
      return;
    } else {
      dispatch(openEditDateTime());
    }
  };

  return (
    <Stack
      direction="column"
      spacing={0}
      justifyContent="flex-start"
      alignItems="flex-start"
      sx={{ width: '100%' }}
    >
      <Typography variant="subtitle2" fontWeight="600" color="#6B7280" fontSize="14px">
        {stop?.stop_type
          ? ` ${StringUtils.getLoadIntermediateStopLabel(stop.stop_type as STOP_TYPE)} scheduled:`
          : ''}
      </Typography>
      <Typography variant="subtitle2" fontWeight="400" color="#1F2937" fontSize="14px">
        {stop?.start_date && DateUtils.getMediumDate(new Date(stop?.start_date))}
        {stop?.start_date && stop?.start_time && ` • `}
        {stop?.start_time && !use24HourClock
          ? DateUtils.get12HourFormattedTime(
              new Date(`${stop.start_date}T${stop.start_time.split('+')[0]}`)
            )
          : stop?.start_time?.slice(0, -3)}
        {isAppointmentWindowEnabled && stop?.end_time && ` - `}
        {isAppointmentWindowEnabled &&
          (stop?.end_time && !use24HourClock
            ? DateUtils.get12HourFormattedTime(
                new Date(`${stop.start_date}T${stop.end_time.split('+')[0]}`)
              )
            : stop?.end_time?.slice(0, -3))}

        {` (${stop?.location ? stop?.location?.short_offset : userData?.short_offset})`}
      </Typography>

      {showInAndOutTimes &&
        ((!isCustomerContact && !isCustomerLink) || isEnabledShowCheckInOutTimes) && (
          <>
            {stop?.in_time &&
              (stop.status === StopStatus.arrived || stop.status === StopStatus.complete) && (
                <CheckedDateTime
                  date={new Date(stop.in_time)}
                  time={new Date(stop.in_time)}
                  label="Actual in time: "
                  overridden={!!stop.is_in_time_overriden}
                  onEditClick={(): void => {
                    handleEditDateTimeClick(stop, 'in', {
                      prevStop,
                      nextStop
                    });
                  }}
                  shortOffset={` (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
                  disableEdition={isCustomerContact || isCustomerLink}
                  use24HourFormat={use24HourClock}
                />
              )}

            {stop?.out_time &&
              (stop.status === StopStatus.arrived || stop.status === StopStatus.complete) && (
                <CheckedDateTime
                  date={new Date(stop.out_time)}
                  time={new Date(stop.out_time)}
                  label="Actual out time: "
                  overridden={!!stop.is_out_time_overriden}
                  onEditClick={(): void =>
                    handleEditDateTimeClick(stop, 'out', { prevStop, nextStop })
                  }
                  shortOffset={` (${DateUtils.getBrowserTimeZoneOffsetNameShort()})`}
                  disableEdition={isCustomerContact || isCustomerLink}
                  use24HourFormat={use24HourClock}
                />
              )}
          </>
        )}

      {stop?.stop_identifier && (
        <Typography variant="subtitle2" fontWeight="400" color="#1F2937" fontSize="14px">
          {stop?.stop_type && getStopLabelType(stop.stop_type as STOP_TYPE)}
          {stop?.stop_identifier}
        </Typography>
      )}
    </Stack>
  );
};

export default ScheduleInfo;
